export type CompanyProfileLocale = 'en' | 'fi' | 'sv';

export interface CompanyProfileUiCopy {
  /** Breadcrumb segment linking to the company hub (currently best employers). */
  breadcrumbCompanies: string;
  summaryHeading: string;
  dataTablesHeading: string;
  metricColumn: string;
  valueColumn: string;
  categoryColumn: string;
  countColumn: string;
  monthColumn: string;
  jobPostingsColumn: string;
  captions: {
    overview: string;
    employerScore: string;
    salary: string;
    workModel: string;
    roles: string;
    locations: string;
    languages: string;
    hiringByMonth: string;
    financials: string;
    registry: string;
  };
  trendGrowing: string;
  trendDeclining: string;
  trendStable: string;
  salaryDataHeading: string;
  medianLabel: string;
  middle50Label: string;
  basedOnListings: string;
  openRolesHeading: string;
  openRolesSub: string;
  seeAllJobs: string;
  companyInfoHeading: string;
  businessId: string;
  status: string;
  founded: string;
  businessLine: string;
  website: string;
  employees: string;
  foundedYearEst: string;
  chartSectionIntro: string;
  noOpenRoles: string;
  workModelChartTitle: string;
  rolesChartTitle: string;
  locationsChartTitle: string;
  jobListingSingular: string;
  jobListingPlural: string;
  notFoundTitle: string;
  notFoundDescription: string;
  /** Employer is not in our CompaniesTable (e.g. Työmarkkinatori-only names). */
  notFoundOutsideCatalogTitle: string;
  notFoundOutsideCatalogDescription: string;
  /** Row exists in CompaniesTable but Algolia profile payload is missing. */
  notFoundProfilePendingTitle: string;
  notFoundProfilePendingDescription: string;
  foundedYearSuffix: string;
  /** Shown in place of the score badge/breakdown when a company has no employerScore yet. */
  employerScorePendingTitle: string;
  employerScorePendingDescription: string;
}

const EN: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Companies',
  summaryHeading: 'Employer overview',
  dataTablesHeading: 'Structured data for this employer',
  metricColumn: 'Metric',
  valueColumn: 'Value',
  categoryColumn: 'Category',
  countColumn: 'Job postings (sample)',
  monthColumn: 'Month',
  jobPostingsColumn: 'Job postings',
  captions: {
    overview: 'Company profile summary',
    employerScore: 'Employer score breakdown',
    salary: 'Salary statistics from job listings',
    workModel: 'Remote, hybrid, and onsite job postings',
    roles: 'Roles companies hired for (aggregated)',
    locations: 'Hiring locations (aggregated)',
    languages: 'Language requirements (aggregated)',
    hiringByMonth: 'Monthly job posting volume',
    financials: 'Reported financial figures (where available)',
    registry: 'Finnish business registry fields',
  },
  trendGrowing: 'Hiring growing',
  trendDeclining: 'Hiring declining',
  trendStable: 'Hiring stable',
  salaryDataHeading: 'Salary data',
  medianLabel: 'Median',
  middle50Label: 'Middle 50%',
  basedOnListings: 'Based on',
  openRolesHeading: 'Open roles',
  openRolesSub:
    'Open listings for this employer in Finland from the JobCrawls jobs index, newest first.',
  seeAllJobs: 'See all jobs',
  companyInfoHeading: 'Company information',
  businessId: 'Business ID',
  status: 'Status',
  founded: 'Registration date',
  businessLine: 'Business line',
  website: 'Website',
  employees: 'employees',
  foundedYearEst: 'Founded',
  chartSectionIntro:
    'Charts below summarise hiring patterns, salary bands, and work arrangements inferred from aggregated job listings.',
  noOpenRoles: 'No open roles at this time.',
  workModelChartTitle: 'Work model',
  rolesChartTitle: 'Roles hired for',
  locationsChartTitle: 'Hiring locations',
  jobListingSingular: 'job listing',
  jobListingPlural: 'job listings',
  notFoundTitle: 'Employer not found',
  notFoundDescription:
    'This employer page could not be loaded. It may have been removed or the link is out of date.',
  notFoundOutsideCatalogTitle: 'We do not have this employer in our directory yet',
  notFoundOutsideCatalogDescription:
    'JobCrawls only builds detailed employer profiles for companies we track in our own directory. This name is not in that set yet — often the case for some Työmarkkinatori listings — so there is no company page to show.',
  notFoundProfilePendingTitle: 'Employer profile not ready yet',
  notFoundProfilePendingDescription:
    'This employer is listed in our directory, but the public profile page is not available yet. Try again later, or browse open roles from search.',
  foundedYearSuffix: ' est.',
  employerScorePendingTitle: 'Not yet rated',
  employerScorePendingDescription:
    "We haven't calculated an Employer Score for this company yet. Check back soon, or browse their open roles below.",
};

const FI: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Yritykset',
  summaryHeading: 'Työnantajan kuvaus',
  dataTablesHeading: 'Rakenteinen tieto työnantajasta',
  metricColumn: 'Tunnusluku',
  valueColumn: 'Arvo',
  categoryColumn: 'Luokka',
  countColumn: 'Ilmoitukset (otos)',
  monthColumn: 'Kuukausi',
  jobPostingsColumn: 'Ilmoitukset',
  captions: {
    overview: 'Yrityksen profiilin yhteenveto',
    employerScore: 'Työnantajapisteet osittain',
    salary: 'Palkkatilastot ilmoituksista',
    workModel: 'Etä-, hybrid- ja lähityöilmoitukset',
    roles: 'Yleisimmät roolit (aggregaatti)',
    locations: 'Rekrytointisijainnit (aggregaatti)',
    languages: 'Kielivaatimukset (aggregaatti)',
    hiringByMonth: 'Kuukausittainen ilmoitusmäärä',
    financials: 'Talousluvut (kun saatavilla)',
    registry: 'Ytj / kaupparekisteri',
  },
  trendGrowing: 'Rekrytointi kasvussa',
  trendDeclining: 'Rekrytointi laskeva',
  trendStable: 'Rekrytointi vakaa',
  salaryDataHeading: 'Palkkatiedot',
  medianLabel: 'Mediaani',
  middle50Label: 'Keskimmäinen 50 %',
  basedOnListings: 'Yhteensä',
  openRolesHeading: 'Avoimet työpaikat',
  openRolesSub:
    'Avoimet ilmoitukset tälle työnantajalle Suomessa JobCrawlsin työpaikkahausta, uusimmat ensin.',
  seeAllJobs: 'Kaikki työpaikat',
  companyInfoHeading: 'Yritystiedot',
  businessId: 'Y-tunnus',
  status: 'Tila',
  founded: 'Rekisteröintipäivä',
  businessLine: 'Toimiala',
  website: 'Verkkosivusto',
  employees: 'työntekijää',
  foundedYearEst: 'Perustettu',
  chartSectionIntro:
    'Kaaviot tiivistävät rekrytointi-, palkka- ja työmallijakaumia aggregoiduista työpaikkailmoituksista.',
  noOpenRoles: 'Ei avoimia työpaikkoja juuri nyt.',
  workModelChartTitle: 'Työmalli',
  rolesChartTitle: 'Yleisimmät roolit',
  locationsChartTitle: 'Rekrytointisijainnit',
  jobListingSingular: 'ilmoitus',
  jobListingPlural: 'ilmoitusta',
  notFoundTitle: 'Työnantajaa ei löytynyt',
  notFoundDescription:
    'Tätä yrityssivua ei voitu ladata. Se on saatettu poistaa tai linkki on vanhentunut.',
  notFoundOutsideCatalogTitle: 'Tätä työnantajaa ei ole vielä hakemistossamme',
  notFoundOutsideCatalogDescription:
    'JobCrawls rakentaa tarkat yrityssivut vain työnantajille, jotka ovat omassa yrityshakemistossamme. Tämä nimi ei ole vielä siinä — tyypillistä joillekin Työmarkkinatori-ilmoituksille — joten yrityssivua ei ole saatavilla.',
  notFoundProfilePendingTitle: 'Yritysprofiili ei ole vielä valmis',
  notFoundProfilePendingDescription:
    'Työnantaja on hakemistossamme, mutta julkinen profiilisivu ei ole vielä käytössä. Kokeile myöhemmin uudelleen tai selaa avoimia työpaikkoja hausta.',
  foundedYearSuffix: '',
  employerScorePendingTitle: 'Ei vielä pisteytetty',
  employerScorePendingDescription:
    'Emme ole vielä laskeneet tälle yritykselle työnantajapisteitä. Käy myöhemmin uudelleen tai selaa avoimia työpaikkoja alta.',
};

const SV: CompanyProfileUiCopy = {
  breadcrumbCompanies: 'Företag',
  summaryHeading: 'Arbetsgivaröversikt',
  dataTablesHeading: 'Strukturerad data om arbetsgivaren',
  metricColumn: 'Mått',
  valueColumn: 'Värde',
  categoryColumn: 'Kategori',
  countColumn: 'Annonser (urval)',
  monthColumn: 'Månad',
  jobPostingsColumn: 'Annonser',
  captions: {
    overview: 'Sammanfattning av företagsprofilen',
    employerScore: 'Arbetsgivarbetyg del för del',
    salary: 'Lönstatistik från annonser',
    workModel: 'Distans-, hybrid- och platsannonser',
    roles: 'Vanligaste roller (aggregerat)',
    locations: 'Rekryteringsorter (aggregerat)',
    languages: 'Språkkrav (aggregerat)',
    hiringByMonth: 'Annonser per månad',
    financials: 'Nyckeltal (när tillgängliga)',
    registry: 'Finskt företagsregister',
  },
  trendGrowing: 'Rekrytering ökar',
  trendDeclining: 'Rekrytering minskar',
  trendStable: 'Rekrytering stabil',
  salaryDataHeading: 'Lönedata',
  medianLabel: 'Median',
  middle50Label: 'Mellersta 50 %',
  basedOnListings: 'Baserat på',
  openRolesHeading: 'Lediga tjänster',
  openRolesSub:
    'Öppna annonser för denna arbetsgivare i Finland från JobCrawls jobbindex, nyast först.',
  seeAllJobs: 'Alla jobb',
  companyInfoHeading: 'Företagsinformation',
  businessId: 'FO-nummer',
  status: 'Status',
  founded: 'Registreringsdatum',
  businessLine: 'Bransch',
  website: 'Webbplats',
  employees: 'anställda',
  foundedYearEst: 'Grundat',
  chartSectionIntro:
    'Diagrammen sammanfattar rekryterings-, löne- och arbetssättsmönster från aggregerade jobbannonser.',
  noOpenRoles: 'Inga lediga tjänster just nu.',
  workModelChartTitle: 'Arbetssätt',
  rolesChartTitle: 'Vanligaste roller',
  locationsChartTitle: 'Rekryteringsorter',
  jobListingSingular: 'annons',
  jobListingPlural: 'annonser',
  notFoundTitle: 'Arbetsgivaren hittades inte',
  notFoundDescription:
    'Den här arbetsgivarsidan kunde inte laddas. Den kan ha tagits bort eller länken är inaktuell.',
  notFoundOutsideCatalogTitle: 'Den här arbetsgivaren finns inte i vår katalog ännu',
  notFoundOutsideCatalogDescription:
    'JobCrawls bygger detaljerade arbetsgivarprofiler bara för företag vi följer i vår egen katalog. Det här namnet finns inte där ännu — vanligt för vissa Työmarkkinatori-annonser — så det finns ingen företagssida att visa.',
  notFoundProfilePendingTitle: 'Arbetsgivarprofilen är inte klar ännu',
  notFoundProfilePendingDescription:
    'Arbetsgivaren finns i vår katalog, men den publika profilsidan är inte tillgänglig ännu. Försök igen senare, eller bläddra bland lediga jobb via sökningen.',
  foundedYearSuffix: '',
  employerScorePendingTitle: 'Inte betygsatt ännu',
  employerScorePendingDescription:
    'Vi har inte beräknat ett arbetsgivarbetyg för det här företaget ännu. Kom tillbaka senare, eller bläddra bland lediga jobb nedan.',
};

export function getCompanyProfileUiCopy(locale: string): CompanyProfileUiCopy {
  if (locale === 'fi') return FI;
  if (locale === 'sv') return SV;
  return EN;
}
